import { gql } from '@apollo/client';
import { useToastStore } from '../stores/toastStore';
import { fetchWithHeaders } from '../api/fetchWithHeaders';
// NOTE: This module now also exposes REST helpers for Spend Analysis (CPV) endpoints.

export type SpendBySupplier = {
  supplier_id: number | null;
  supplier_name: string;
  total_cost: number;
  total_volume: number;
  avg_price: number | null;
};

export type SpendByCountry = {
  country: string | null;
  total_cost: number;
  total_volume: number;
  avg_price: number | null;
};

export type SpendByYear = {
  year: number;
  total_cost: number;
  total_volume: number;
  avg_price: number | null;
};

const apiBase = import.meta.env.VITE_API_BASE_URL as string;

function spendPath(kind: 'supplier'|'country'|'year', negotiationId?: number) {
  if (negotiationId) {
    // Preferred path-param style
    return `${apiBase}api/v1/negotiation-packages/${encodeURIComponent(String(negotiationId))}/spend-analysis/by-${kind}`;
  }
  // Fallback to unscoped endpoints (still supported in backend)
  return `${apiBase}api/v1/negotiation-packages/spend-analysis/by-${kind}`;
}

export async function fetchSpendBySupplier(negotiationId?: number): Promise<SpendBySupplier[]> {
  const {error} = useToastStore.getState();
  const url = spendPath('supplier', negotiationId);
  const res = await fetchWithHeaders(url, {method: 'GET'});
  if (!res.ok){
    error("Error", `Failed to fetch spend by supplier: ${res.statusText}`);
    throw new Error(`Failed to fetch spend by supplier: ${res.statusText}`);
  };
  return res.json();
}

export async function fetchSpendByCountry(negotiationId?: number): Promise<SpendByCountry[]> {
  const {error} = useToastStore.getState();
  const url = spendPath('country', negotiationId);
  const res = await fetchWithHeaders(url, {method: 'GET'});
  if (!res.ok) {
    error("Error", `Failed to fetch spend by country: ${res.statusText}`);
    throw new Error(`Failed to fetch spend by country: ${res.statusText}`);
  };
  return res.json();
}

export async function fetchSpendByYear(negotiationId?: number): Promise<SpendByYear[]> {
  const {error} = useToastStore.getState();
  const url = spendPath('year', negotiationId);
  const res = await fetchWithHeaders(url, {method: 'GET'});
  if (!res.ok) {
    error("Error", `Failed to fetch spend by year: ${res.statusText}`);
    throw new Error(`Failed to fetch spend by year: ${res.statusText}`);
  };
  return res.json();
}

export const GET_NEGOTIATIONS = gql`
  query GetNegotiations {
    negotiations {
      supplier
      price
      productName
    }
  }
`;

export const GET_NEGOTIATION = gql`
  query GetNegotiation($supplier: String!) {
    negotiation(supplier: $supplier) {
      supplier
      price
      productName
    }
  }
`;

export const CREATE_NEGOTIATION = gql`
  mutation CreateNegotiation($negotiationInput: NegotiationInput!) {
    createNegotiation(negotiationInput: $negotiationInput) {
      supplier
      price
      productName
    }
  }
`;

export const UPDATE_NEGOTIATION = gql`
  mutation UpdateNegotiation($supplier: String!, $negotiationInput: NegotiationInput!) {
    updateNegotiation(supplier: $supplier, negotiationInput: $negotiationInput) {
      supplier
      price
      productName
    }
  }
`;

export const DELETE_NEGOTIATION = gql`
  mutation DeleteNegotiation($supplier: String!) {
    deleteNegotiation(supplier: $supplier)
  }
`;

export const NEGOTIATION_UPDATES_SUBSCRIPTION = gql`
  subscription NegotiationUpdates {
    negotiationUpdates {
      supplier
      price
      productName
    }
  }
`;